import React, { ComponentType, ReactNode, Suspense } from 'react';
import { Flex, Spinner } from 'core/components';
import Navbar from 'app/components/navbar/Navbar';

type ImportComponentFn<P> = () => Promise<{ default: ComponentType<P> }>;
interface LazyLoadOptions {
  enablePageNav?: boolean;
  fallback?: ReactNode;
}
interface RetryOrReloadOptions {
  retriesLeft?: number;
  interval?: number;
}

const createFallback = ({ enablePageNav = false }) => (
  <>
    {enablePageNav && <Navbar bg="navbarBackground" onMenuToggle={() => {}} />}
    <Flex flex="1 1 100%" alignItems="center" justifyContent="center">
      <Spinner />
    </Flex>
  </>
);

function retryOrReload<P extends object>(
  fn: ImportComponentFn<P>,
  options: RetryOrReloadOptions = {}
): Promise<{ default: ComponentType<P> }> {
  const { retriesLeft = 1, interval = 1000 } = options;
  const name = `chunkRefreshed${window.location.pathname}`;

  return new Promise((resolve, reject) => {
    fn()
      .then((...args) => {
        if (window.localStorage) {
          window.localStorage.removeItem(name);
        }
        return resolve(...args);
      })
      .catch((error) => {
        setTimeout(() => {
          if (retriesLeft === 0) {
            if (window.localStorage && !window.localStorage.getItem(name)) {
              window.localStorage.setItem(name, 'true');
              console.info('Setting', name);
              // @ts-expect-error passing true to force reloads is only supported in firefox, I guess TS considers it a hack
              window.location.reload(true);
            } else {
              reject(error);
            }
          } else {
            console.info('Retrying', name);
            retryOrReload(fn, { retriesLeft: retriesLeft - 1, interval }).then(resolve, reject);
          }
        }, interval);
      });
  });
}

export default function lazyLoad<P extends object>(
  fn: ImportComponentFn<P>,
  { enablePageNav = false, fallback = null }: LazyLoadOptions = {}
) {
  const Component = React.lazy<ComponentType<P>>(() => retryOrReload(fn));

  return (props: P) => (
    <Suspense fallback={React.isValidElement(fallback) ? fallback : createFallback({ enablePageNav })}>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <Component {...(props as any)} />
    </Suspense>
  );
}
